import { useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { privacyApi } from '../../../api/privacy.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const LEGAL_BASES = [
  { value: 'consent', label: 'Consenso (art. 6.1.a)' },
  { value: 'contract', label: 'Esecuzione contratto (art. 6.1.b)' },
  { value: 'legal_obligation', label: 'Obbligo legale (art. 6.1.c)' },
  { value: 'vital_interests', label: 'Interessi vitali (art. 6.1.d)' },
  { value: 'public_task', label: 'Compito di interesse pubblico (art. 6.1.e)' },
  { value: 'legitimate_interests', label: 'Legittimo interesse (art. 6.1.f)' }
];

const EMPTY = {
  name: '',
  description: '',
  controllerName: '',
  controllerEmail: '',
  controllerPhone: '',
  dpoName: '',
  dpoEmail: '',
  dpoPhone: '',
  purposes: [],
  legalBases: ['consent'],
  legalBasisNotes: '',
  dataCategories: [],
  dataSubjects: [],
  specialCategories: [],
  recipients: [],
  processors: [],
  retentionPolicy: '',
  retentionDays: '',
  internationalTransfers: [],
  securityMeasures: [],
  riskAssessment: '',
  dpiaRequired: 'false',
  dpiaReference: '',
  status: 'active',
  notes: ''
};

export function RegisterFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  const { register, handleSubmit, control, reset, watch, setValue, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });
  const transfers = useFieldArray({ control, name: 'internationalTransfers' });
  const legalBases = watch('legalBases');

  useEffect(() => {
    if (!open) return;
    reset(initial ? {
      name: initial.name || '',
      description: initial.description || '',
      controllerName: initial.controller?.name || '',
      controllerEmail: initial.controller?.email || '',
      controllerPhone: initial.controller?.phone || '',
      dpoName: initial.dpo?.name || '',
      dpoEmail: initial.dpo?.email || '',
      dpoPhone: initial.dpo?.phone || '',
      purposes: initial.purposes || [],
      legalBases: initial.legalBases?.length ? initial.legalBases : ['consent'],
      legalBasisNotes: initial.legalBasisNotes || '',
      dataCategories: initial.dataCategories || [],
      dataSubjects: initial.dataSubjects || [],
      specialCategories: initial.specialCategories || [],
      recipients: initial.recipients || [],
      processors: initial.processors || [],
      retentionPolicy: initial.retentionPolicy || '',
      retentionDays: initial.retentionDays ?? '',
      internationalTransfers: initial.internationalTransfers || [],
      securityMeasures: initial.securityMeasures || [],
      riskAssessment: initial.riskAssessment || '',
      dpiaRequired: String(Boolean(initial.dpiaRequired)),
      dpiaReference: initial.dpiaReference || '',
      status: initial.status || 'active',
      notes: initial.notes || ''
    } : EMPTY);
  }, [open, initial, reset]);

  const toggleBase = (val) => {
    const next = legalBases.includes(val)
      ? legalBases.filter((b) => b !== val)
      : [...legalBases, val];
    setValue('legalBases', next, { shouldDirty: true });
  };

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        name: values.name,
        description: values.description || undefined,
        controller: clean({ name: values.controllerName, email: values.controllerEmail, phone: values.controllerPhone }),
        dpo: clean({ name: values.dpoName, email: values.dpoEmail, phone: values.dpoPhone }),
        purposes: values.purposes,
        legalBases: values.legalBases,
        legalBasisNotes: values.legalBasisNotes || undefined,
        dataCategories: values.dataCategories,
        dataSubjects: values.dataSubjects,
        specialCategories: values.specialCategories,
        recipients: values.recipients,
        processors: values.processors,
        retentionPolicy: values.retentionPolicy || undefined,
        retentionDays: values.retentionDays === '' ? undefined : Number(values.retentionDays),
        internationalTransfers: (values.internationalTransfers || [])
          .filter((r) => r.recipient && r.recipient.trim().length)
          .map((r) => ({
            recipient: r.recipient,
            country: r.country || undefined,
            safeguards: r.safeguards || undefined
          })),
        securityMeasures: values.securityMeasures,
        riskAssessment: values.riskAssessment || undefined,
        dpiaRequired: values.dpiaRequired === 'true',
        dpiaReference: values.dpiaReference || undefined,
        status: values.status,
        notes: values.notes || undefined
      };
      for (const k of Object.keys(payload)) {
        if (payload[k] === undefined || (Array.isArray(payload[k]) && payload[k].length === 0)) {
          // Empty arrays are valid in update mode; keep them on create only when present.
          if (editing) delete payload[k];
        }
      }
      return editing ? privacyApi.updateRegister(initial._id, payload) : privacyApi.createRegister(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Voce aggiornata.' : 'Voce creata.');
      queryClient.invalidateQueries({ queryKey: ['privacy', 'register'] });
      queryClient.invalidateQueries({ queryKey: ['privacy', 'overview'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? `Modifica "${initial?.name}"` : 'Nuova attività di trattamento'}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="privacy-register-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="privacy-register-form" className="flex flex-col gap-5" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Nome attività" error={errors.name?.message} {...register('name', { required: 'Nome richiesto' })} />
          <Select label="Stato" {...register('status')}>
            <option value="active">Attiva</option>
            <option value="draft">Bozza</option>
            <option value="retired">Dismessa</option>
          </Select>
          <div className="sm:col-span-2">
            <Textarea label="Descrizione" rows={2} error={errors.description?.message} {...register('description')} />
          </div>
        </section>

        <Section title="Titolare del trattamento">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input label="Nome" {...register('controllerName')} />
            <Input label="Email" type="email" {...register('controllerEmail')} />
            <Input label="Telefono" {...register('controllerPhone')} />
          </div>
        </Section>

        <Section title="DPO / Responsabile protezione dati">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input label="Nome" {...register('dpoName')} />
            <Input label="Email" type="email" {...register('dpoEmail')} />
            <Input label="Telefono" {...register('dpoPhone')} />
          </div>
        </Section>

        <Section title="Finalità e base giuridica">
          <Controller
            control={control}
            name="purposes"
            render={({ field }) => <TagInput label="Finalità" placeholder="es. fornire raccomandazioni AI" value={field.value} onChange={field.onChange} />}
          />
          <div className="mt-3">
            <p className="mb-2 text-sm font-medium text-ink">Basi giuridiche</p>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {LEGAL_BASES.map((b) => (
                <label key={b.value} className="flex items-start gap-2 text-xs text-ink">
                  <input
                    type="checkbox"
                    checked={legalBases.includes(b.value)}
                    onChange={() => toggleBase(b.value)}
                    className="mt-0.5"
                  />
                  <span>{b.label}</span>
                </label>
              ))}
            </div>
          </div>
          <Textarea className="mt-3" label="Note sulla base giuridica" rows={2} {...register('legalBasisNotes')} />
        </Section>

        <Section title="Categorie di dati e interessati">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="dataCategories"
              render={({ field }) => <TagInput label="Categorie di dati" placeholder="es. indirizzo IP" value={field.value} onChange={field.onChange} />}
            />
            <Controller
              control={control}
              name="dataSubjects"
              render={({ field }) => <TagInput label="Categorie di interessati" placeholder="es. clienti walk-in" value={field.value} onChange={field.onChange} />}
            />
            <Controller
              control={control}
              name="specialCategories"
              render={({ field }) => <TagInput label="Categorie particolari (art. 9)" placeholder="lasciare vuoto se non applicabile" value={field.value} onChange={field.onChange} />}
            />
          </div>
        </Section>

        <Section title="Destinatari e responsabili esterni">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="recipients"
              render={({ field }) => <TagInput label="Destinatari" value={field.value} onChange={field.onChange} />}
            />
            <Controller
              control={control}
              name="processors"
              render={({ field }) => <TagInput label="Responsabili (processor)" value={field.value} onChange={field.onChange} />}
            />
          </div>
        </Section>

        <Section title="Conservazione">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Textarea label="Politica di conservazione" rows={2} {...register('retentionPolicy')} />
            </div>
            <Input label="Giorni di conservazione" type="number" min="0" max="36500" {...register('retentionDays')} />
          </div>
        </Section>

        <Section title="Trasferimenti extra UE">
          {transfers.fields.length === 0 ? (
            <p className="text-xs text-ink-muted">Nessun trasferimento dichiarato.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {transfers.fields.map((f, idx) => (
                <div key={f.id} className="grid grid-cols-1 gap-2 rounded-md border border-border bg-surface p-3 sm:grid-cols-[1fr_120px_1fr_auto]">
                  <Input label="Destinatario" {...register(`internationalTransfers.${idx}.recipient`, { required: 'richiesto' })} />
                  <Input label="Paese" placeholder="US, UK..." {...register(`internationalTransfers.${idx}.country`)} />
                  <Input label="Garanzie" placeholder="SCC, BCR..." {...register(`internationalTransfers.${idx}.safeguards`)} />
                  <button type="button" className="self-end text-xs text-danger hover:underline" onClick={() => transfers.remove(idx)}>Rimuovi</button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            className="mt-2 text-xs text-brand hover:text-brand-hover"
            onClick={() => transfers.append({ recipient: '', country: '', safeguards: '' })}
          >
            + Aggiungi trasferimento
          </button>
        </Section>

        <Section title="Misure di sicurezza e DPIA">
          <Controller
            control={control}
            name="securityMeasures"
            render={({ field }) => <TagInput label="Misure di sicurezza" placeholder="es. TLS 1.2+, crittografia a riposo" value={field.value} onChange={field.onChange} />}
          />
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Select label="DPIA richiesta" {...register('dpiaRequired')}>
              <option value="false">No</option>
              <option value="true">Sì</option>
            </Select>
            <div className="sm:col-span-2">
              <Input label="Riferimento DPIA" placeholder="link interno o codice documento" {...register('dpiaReference')} />
            </div>
            <div className="sm:col-span-3">
              <Textarea label="Valutazione del rischio" rows={2} {...register('riskAssessment')} />
            </div>
          </div>
        </Section>

        <Textarea label="Note interne" rows={2} {...register('notes')} />
      </form>
    </Modal>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-md border border-border bg-surface p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-muted">{title}</p>
      {children}
    </section>
  );
}

function clean(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}
